import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Observable } from 'rxjs';
import { Department, FindUniversityPapersResult } from 'src/app/models/find-university-papers-result';
import { GoToPaperDetailParams } from '../../../models/search-univ-interfaces';
import { ExamType } from '../../../models/university-search-condition';
import { HIDE_PASSNAVI_LINK_UNIVERSITY_IDS } from '../../../resources/common-id/hide-passnavi-link-university-ids';

export interface ExamTypeSection {
  examType: ExamType;
  label: string;
  departments: Department[];
}

@Component({
  selector: 'app-search-univ-result-base',
  templateUrl: './search-univ-result-base.component.html',
  styleUrls: ['./search-univ-result-base.component.scss']
})
export class SearchUnivResultBaseComponent implements OnChanges {
  @Input() searchResults: FindUniversityPapersResult[];
  @Input() isPremiumIconShown: boolean;
  @Input() isBtoC: boolean;
  @Input() buttonAbled$: Observable<boolean>;
  @Input() bookmarkClickEmitted: boolean;
  @Input() errorUnivId$: Observable<string>;
  @Input() selectedExamType: ExamType = ExamType.ALL;
  @Output() goToPaperDetailClick = new EventEmitter<GoToPaperDetailParams>();
  @Output() goToUnivDetailClick = new EventEmitter<string>();
  @Output() addUniversityBookmarkClick = new EventEmitter<string>();
  @Output() deleteUniversityBookmarkClick = new EventEmitter<string>();

  hidePassnaviLinkUniversityIds = HIDE_PASSNAVI_LINK_UNIVERSITY_IDS;
  ExamType = ExamType;

  // 大学IDをキー、入試方式セクションの配列を値とするオブジェクト。大学ごとに、入試方式セクションの表示・非表示や、セクション内の学部の絞り込みを行うために使用する。
  sectionsByUniversityId: { [universityId: string]: ExamTypeSection[] } = {};

  private readonly sectionDefinitions: Array<{ examType: ExamType; label: string }> = [
    { examType: ExamType.GENERAL, label: '一般入試' },
    { examType: ExamType.SPECIAL_ADMISSIONS, label: '推薦・総合・その他の入試' }
  ];

  ngOnChanges(changes: SimpleChanges) {
    if (changes.searchResults) {
      this.editSectionsByUniversityId();
    }
  }

  //目的: 配列を作り直しても、論理的に同じアイテムの DOM を使い回す
  //必要性: 「もっと見る」や ngOnChanges のたびに走る配列再代入で、アコーディオンの再アニメーション（チラつき）を防ぐ
  trackByUniversityId(_index: number, result: FindUniversityPapersResult): string {
    return result.universityId;
  }

  trackBySectionExamType(_index: number, section: ExamTypeSection): ExamType {
    return section.examType;
  }

  trackByDepartmentName(_index: number, dept: Department): string {
    return dept.deptName;
  }

  private editSectionsByUniversityId() {
    const results = this.searchResults || [];
    const nextSections: { [universityId: string]: ExamTypeSection[] } = {};
    results.forEach(result => {
      nextSections[result.universityId] = this.sectionDefinitions
        .filter(def => this.isSectionAllowedByExamType(def.examType))
        .map(def => ({
          examType: def.examType,
          label: def.label,
          departments: this.filterDepartmentsByExamType(result.departments, def.examType)
        }))
        .filter(section => section.departments.length > 0);
    });
    this.sectionsByUniversityId = nextSections;
  }

  private isSectionAllowedByExamType(examType: ExamType): boolean {
    if (this.selectedExamType === ExamType.GENERAL && examType !== ExamType.GENERAL) return false;
    if (this.selectedExamType === ExamType.SPECIAL_ADMISSIONS && examType !== ExamType.SPECIAL_ADMISSIONS) return false;
    return true;
  }

  // 入試方式で papers を絞り込んで学部一覧を返す。該当 papers が無い学部は除外する
  private filterDepartmentsByExamType(departments: Department[], examType: ExamType): Department[] {
    return departments
      .map(department => ({
        ...department,
        papers: department.papers.filter(paper => paper.examType === examType)
      }))
      .filter(department => department.papers.length > 0);
  }
}
